import {
  useEffect, useRef, useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import { TILE_SIZE } from '../../../game/events';
import { unreachableTiles } from '../../../game/reachable';
import { compileZones } from '../../../game/zoneShapes';
import { tileSourceRect } from '../../../game/tileSheet';

/**
 * The surface you draw a world on.
 *
 * CANVAS, NOT DOM. An 80x60 world is 4,800 cells across three layers; as
 * elements that is 14,400 nodes re-styled on every brush stroke. The same
 * reasoning as `Minimap` and `RoutePreview`, both of which draw tiles this way
 * already.
 *
 * IT DRAWS BLOCKS, NOT ART, and that is deliberate rather than unfinished. The
 * tileset is a 561-byte placeholder with five tiles — there is no picture of a
 * wall worth showing at this size. Solid colour with a legend says exactly as
 * much and stays readable when the whole world is on screen.
 *
 * WHAT IT DOES NOT DO: it holds no state of its own beyond the drag. Every
 * edit goes out through `onPaint`/`onPlace` and comes back as new props, so
 * undo lives in one place (`useWorldDraft`) rather than being split between a
 * canvas and a page.
 */

const COLOUR = {
  // Under the art, where a tile is transparent or absent. Distinct from
  // `floor` so an unpainted tile reads as a hole rather than as ground.
  empty: '#0b1120',
  floor: '#1f2937',
  floorAlt: '#243244',
  wall: '#64748b',
  above: '#7c3aed',
  grid: 'rgba(148,163,184,0.13)',
  spawn: '#34d399',
  portal: '#f5d97a',
  sign: '#38bdf8',
  stranded: 'rgba(239,68,68,0.38)',
  zone: '#f472b6',
  zoneSelected: '#fbbf24',
  drag: 'rgba(244,114,182,0.35)',
};

function WorldCanvas({
  world, tool, brushGid, layer, zoom, showUnreachable, showArt, sheet, tileset,
  selectedZoneId, onPaint, onPlace, onPickAt, onZoneDrag, onZoneClick,
}) {
  const canvasRef = useRef(null);
  /** Where a zone drag started, in tiles. Null when not dragging one out. */
  const [zoneDrag, setZoneDrag] = useState(null);
  /**
   * Bumped when the brush comes up, purely to re-run the draw effect.
   *
   * The expensive overlay below is skipped while a stroke is in progress, and
   * `dragging` is a ref — mutating it renders nothing. Without this, letting go
   * after a drag that painted no new tile would leave the overlay stale.
   */
  const [settled, setSettled] = useState(0);
  // Which tile the last drag event painted, so holding the mouse down does not
  // push one undo entry per mousemove — a single stroke should be one undo.
  const lastTile = useRef(null);
  const dragging = useRef(false);

  const { widthTiles, heightTiles } = world;
  const cell = Math.max(2, Math.round(zoom));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // jsdom — draw nothing rather than throw

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const { floor, above } = world.layers;

    // THE COMPILED WALLS, not the painted ones. A zone's walls exist only after
    // compilation, so drawing the raw layer would show a building as an empty
    // outline and disagree with both the game and the reachability shading.
    const { zones } = world;
    const walls = compileZones(world.layers.walls, zones, widthTiles, heightTiles);

    // Art needs a loaded sheet; without one this falls through to blocks rather
    // than leaving the world blank. See `useTileSheet`.
    const drawArt = showArt && sheet?.ready && sheet.image;
    const sheetInfo = {
      columns: tileset?.columns || 16,
      firstgid: tileset?.firstgid ?? 1,
      tileSize: TILE_SIZE,
    };

    // Tiles are 16px source drawn into a `cell`-px box, and `cell` goes as low
    // as 4. Smoothing turns that into mush; nearest-neighbour keeps the pixels.
    ctx.imageSmoothingEnabled = false;

    /** One tile of art, or false if there was nothing to draw. */
    const art = (gid, x, y) => {
      const src = tileSourceRect(gid, sheetInfo);
      if (!src) return false;
      ctx.drawImage(sheet.image, src.sx, src.sy, src.size, src.size, x, y, cell, cell);
      return true;
    };

    for (let ty = 0; ty < heightTiles; ty += 1) {
      for (let tx = 0; tx < widthTiles; tx += 1) {
        const i = ty * widthTiles + tx;
        const x = tx * cell;
        const y = ty * cell;

        // Floor first, then walls over it, then `above` as a translucent wash —
        // matching the order the game renderer draws them in, so the editor
        // cannot show a stacking that the game will not.
        if (drawArt) {
          // A backing fill under every tile: art can be transparent (a fence,
          // a tree), and without this the previous frame would show through.
          ctx.fillStyle = COLOUR.empty;
          ctx.fillRect(x, y, cell, cell);
          art(floor[i], x, y);
          art(walls[i], x, y);
          if (above[i] !== 0) {
            // Still translucent, because `above` draws OVER players in game —
            // showing it opaque here would hide what is underneath and make it
            // look like a wall.
            ctx.globalAlpha = 0.75;
            art(above[i], x, y);
            ctx.globalAlpha = 1;
          }
        } else {
          ctx.fillStyle = floor[i] === 2 ? COLOUR.floorAlt : COLOUR.floor;
          ctx.fillRect(x, y, cell, cell);

          if (walls[i] !== 0) {
            ctx.fillStyle = COLOUR.wall;
            ctx.fillRect(x, y, cell, cell);
          }

          if (above[i] !== 0) {
            ctx.fillStyle = COLOUR.above;
            ctx.globalAlpha = 0.55;
            ctx.fillRect(x, y, cell, cell);
            ctx.globalAlpha = 1;
          }
        }
      }
    }

    // Anywhere a player cannot actually get to. Computed with the real
    // pathfinder, so it honours the rule that a one-tile-wide corridor is
    // impassable even though every tile in it is empty.
    /**
     * NOT WHILE THE BRUSH IS DOWN. This is a flood fill over the pathfinder's
     * node grid — two nodes per tile horizontally — so on a 160x120 city it is
     * ~125,000 nodes, and the draw effect re-runs on EVERY painted tile. That
     * turned a drag across a street into a slideshow.
     *
     * Skipping it mid-stroke costs nothing real: the answer is only interesting
     * once you have finished drawing, and `settled` forces one final pass when
     * the brush comes up.
     */
    if (showUnreachable && !dragging.current) {
      ctx.fillStyle = COLOUR.stranded;
      unreachableTiles(
        { walls, width: widthTiles, height: heightTiles },
        world.spawns,
      ).forEach((i) => {
        ctx.fillRect((i % widthTiles) * cell, Math.floor(i / widthTiles) * cell, cell, cell);
      });
    }

    // Grid only when the cells are big enough for it to mean anything.
    if (cell >= 8) {
      ctx.strokeStyle = COLOUR.grid;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let tx = 0; tx <= widthTiles; tx += 1) {
        ctx.moveTo(tx * cell + 0.5, 0);
        ctx.lineTo(tx * cell + 0.5, heightTiles * cell);
      }
      for (let ty = 0; ty <= heightTiles; ty += 1) {
        ctx.moveTo(0, ty * cell + 0.5);
        ctx.lineTo(widthTiles * cell, ty * cell + 0.5);
      }
      ctx.stroke();
    }

    const scale = cell / TILE_SIZE;

    // Portals are rectangles anchored top-left; spawns and signs are points.
    // Mixing those up puts a portal half a room from where it was drawn, which
    // is the note map-spec.md opens the portal section with.
    world.portals.forEach((p) => {
      ctx.strokeStyle = COLOUR.portal;
      ctx.lineWidth = 2;
      ctx.strokeRect(p.x * scale, p.y * scale, p.width * scale, p.height * scale);
    });

    const marker = (x, y, colour, glyph) => {
      const px = x * scale;
      const py = y * scale;
      ctx.beginPath();
      ctx.arc(px, py, Math.max(3, cell * 0.35), 0, Math.PI * 2);
      ctx.fillStyle = colour;
      ctx.fill();

      if (cell >= 10) {
        ctx.fillStyle = '#0b1120';
        ctx.font = `bold ${Math.round(cell * 0.5)}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(glyph, px, py);
      }
    };

    world.spawns.forEach((s) => marker(s.x, s.y, COLOUR.spawn, 'S'));
    world.signs.forEach((s) => marker(s.x, s.y, COLOUR.sign, '?'));

    // Zones last, over everything, because the label has to be readable against
    // whatever is underneath it.
    world.zones.forEach((z) => {
      const selected = z.id === selectedZoneId;
      ctx.strokeStyle = selected ? COLOUR.zoneSelected : COLOUR.zone;
      ctx.lineWidth = selected ? 3 : 2;
      ctx.setLineDash(selected ? [] : [4, 3]);
      ctx.strokeRect(z.x * cell + 1, z.y * cell + 1, z.w * cell - 2, z.h * cell - 2);
      ctx.setLineDash([]);

      const label = z.label || '(unnamed)';
      if (cell >= 6) {
        const cx = (z.x + z.w / 2) * cell;
        const cy = (z.y + z.h / 2) * cell;
        ctx.font = `bold ${Math.max(9, Math.round(cell * 0.8))}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // A slab behind the text: a building's inside is whatever the brush
        // left it, and a label on bare floor is often unreadable.
        const box = ctx.measureText(label).width + 8;
        ctx.fillStyle = 'rgba(2,6,23,0.75)';
        ctx.fillRect(cx - box / 2, cy - cell * 0.55, box, cell * 1.1);

        ctx.fillStyle = selected ? COLOUR.zoneSelected : COLOUR.zone;
        ctx.fillText(label, cx, cy);
      }
    });

    // The rectangle being dragged out right now.
    if (zoneDrag) {
      const x = Math.min(zoneDrag.fromTx, zoneDrag.tx);
      const y = Math.min(zoneDrag.fromTy, zoneDrag.ty);
      const w = Math.abs(zoneDrag.tx - zoneDrag.fromTx) + 1;
      const h = Math.abs(zoneDrag.ty - zoneDrag.fromTy) + 1;

      ctx.fillStyle = COLOUR.drag;
      ctx.fillRect(x * cell, y * cell, w * cell, h * cell);
      ctx.strokeStyle = COLOUR.zone;
      ctx.lineWidth = 2;
      ctx.strokeRect(x * cell, y * cell, w * cell, h * cell);
    }
    // `sheet.ready` rather than `sheet`: the object identity changes on every
    // load phase, but only becoming ready changes what gets drawn.
    // `settled` is here for its side effect only — it forces the one redraw
    // that fills the unreachable overlay back in after a stroke ends.
  }, [world, cell, widthTiles, heightTiles, showUnreachable, selectedZoneId, zoneDrag,
    showArt, sheet?.ready, sheet?.image, tileset, settled]);

  /** Canvas pixels -> tile coordinates. One place, so a click and a drag can
   *  never disagree about which tile is under the pointer. */
  const tileAt = useCallback((event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * canvas.width;
    const py = ((event.clientY - rect.top) / rect.height) * canvas.height;

    return {
      tx: Math.floor(px / cell),
      ty: Math.floor(py / cell),
      // World units, for the things that are not tile-aligned.
      x: Math.round((px / cell) * TILE_SIZE),
      y: Math.round((py / cell) * TILE_SIZE),
    };
  }, [cell]);

  const apply = useCallback((event, isStart) => {
    const at = tileAt(event);
    if (at.tx < 0 || at.ty < 0 || at.tx >= widthTiles || at.ty >= heightTiles) return;

    if (tool === 'zone') {
      if (isStart) {
        // Clicking inside an existing building selects it rather than starting
        // a new one on top — dragging a second zone over the first is almost
        // always a misclick, and the inspector is what you wanted.
        const existing = onZoneClick(at.tx, at.ty);
        if (existing) { setZoneDrag(null); return; }
        setZoneDrag({
          fromTx: at.tx, fromTy: at.ty, tx: at.tx, ty: at.ty,
        });
      } else if (zoneDrag) {
        setZoneDrag((d) => ({ ...d, tx: at.tx, ty: at.ty }));
      }
      return;
    }

    if (tool === 'paint' || tool === 'erase') {
      const id = `${at.tx},${at.ty}`;
      if (lastTile.current === id) return; // same tile, same stroke — nothing new
      lastTile.current = id;
      onPaint({
        tx: at.tx, ty: at.ty, layer, gid: tool === 'erase' ? 0 : brushGid, isStart,
      });
      return;
    }

    // Placing is a click, not a drag — repeating it on mousemove would scatter
    // a spawn point per pixel travelled.
    if (isStart) {
      if (tool === 'pick') onPickAt(at);
      else onPlace({ tool, ...at });
    }
  }, [tool, layer, brushGid, widthTiles, heightTiles, tileAt,
    onPaint, onPlace, onPickAt, onZoneClick, zoneDrag]);

  /** Finish a zone drag. Anything smaller than 2x2 has no inside, so it is
   *  discarded rather than created as something `compileZones` will skip. */
  const endDrag = useCallback(() => {
    if (zoneDrag) {
      const w = Math.abs(zoneDrag.tx - zoneDrag.fromTx) + 1;
      const h = Math.abs(zoneDrag.ty - zoneDrag.fromTy) + 1;
      if (w >= 2 && h >= 2) {
        onZoneDrag({
          tx: Math.min(zoneDrag.fromTx, zoneDrag.tx),
          ty: Math.min(zoneDrag.fromTy, zoneDrag.ty),
          w,
          h,
        });
      }
      setZoneDrag(null);
    }
    dragging.current = false;
    lastTile.current = null;
    setSettled((n) => n + 1);
  }, [zoneDrag, onZoneDrag]);

  return (
    <div className="overflow-auto rounded border border-gray-300 bg-gray-950 dark:border-gray-700">
      <canvas
        ref={canvasRef}
        width={widthTiles * cell}
        height={heightTiles * cell}
        className="block cursor-crosshair"
        onMouseDown={(e) => {
          dragging.current = true;
          lastTile.current = null;
          apply(e, true);
        }}
        onMouseMove={(e) => { if (dragging.current) apply(e, false); }}
        onMouseUp={endDrag}
        // Releasing outside the canvas would otherwise leave the brush stuck
        // down, painting whenever the pointer wandered back.
        onMouseLeave={endDrag}
      />
    </div>
  );
}

const point = PropTypes.shape({ x: PropTypes.number, y: PropTypes.number });

WorldCanvas.propTypes = {
  world: PropTypes.shape({
    widthTiles: PropTypes.number.isRequired,
    heightTiles: PropTypes.number.isRequired,
    layers: PropTypes.shape({
      floor: PropTypes.arrayOf(PropTypes.number),
      walls: PropTypes.arrayOf(PropTypes.number),
      above: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    spawns: PropTypes.arrayOf(point),
    zones: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      x: PropTypes.number,
      y: PropTypes.number,
      w: PropTypes.number,
      h: PropTypes.number,
    })),
    portals: PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
      toMap: PropTypes.string,
    })),
    signs: PropTypes.arrayOf(point),
  }).isRequired,
  tool: PropTypes.string.isRequired,
  brushGid: PropTypes.number.isRequired,
  layer: PropTypes.string.isRequired,
  /** Pixels per tile. */
  zoom: PropTypes.number,
  showUnreachable: PropTypes.bool,
  /** Draw the real tiles instead of flat colour blocks. */
  showArt: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  sheet: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  tileset: PropTypes.object,
  selectedZoneId: PropTypes.string,
  onPaint: PropTypes.func.isRequired,
  onPlace: PropTypes.func.isRequired,
  onPickAt: PropTypes.func.isRequired,
  /** A finished drag, in tiles. */
  onZoneDrag: PropTypes.func.isRequired,
  /** Returns the zone at these tiles, or null. Selecting is its side effect. */
  onZoneClick: PropTypes.func.isRequired,
};

WorldCanvas.defaultProps = {
  zoom: 12,
  showUnreachable: false,
  showArt: false,
  sheet: null,
  tileset: null,
  selectedZoneId: null,
};

export default WorldCanvas;
